// ── Billing / entitlement ─────────────────────────────────────────────────────
// Reads users/{uid}/billing/status, a document ONLY ever written server-side
// (see api/payments/webhook.js, api/payments/webhook-ls.js and firestore.rules
// — client writes to it are rejected outright) so a signed-in user can never
// grant themselves premium by editing Firestore from devtools. Also kicks off
// the checkout redirect via the server (PayMongo for PH, Lemon Squeezy for
// everyone else), so neither provider's secret key ever reaches the browser.
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { db, auth, isFirebaseConfigured } from './config';
import { useAuth } from './auth';
import { isPlayBillingAvailable, purchasePlayBillingPlan } from './playBilling';
import { apiUrl } from './apiUrl';

// Instant, offline fallback for whether the visitor is in the Philippines —
// used only as the very first render's guess, before detectCountry() below
// has a chance to come back with the real answer. Never used for anything
// security-sensitive.
// Worked out once and remembered: the first Intl.DateTimeFormat() of an app run
// loads the browser's time-zone data, which cost ~70ms (6x-throttled) inside the
// Upgrade tap and ate the purchase page's fade-in (Jade, 2026-10-11).
let isPHGuess = null;
export function guessIsPH() {
  if (isPHGuess !== null) return isPHGuess;
  let ph = false;
  try {
    ph = Intl.DateTimeFormat().resolvedOptions().timeZone === 'Asia/Manila';
  } catch { /* Intl unsupported — fall through to locale check */ }
  if (!ph) ph = (navigator.languages || [navigator.language || '']).some((l) => l.toLowerCase().endsWith('-ph'));
  isPHGuess = ph;
  return ph;
}

// Asks the server which country Vercel's edge network saw the request come
// from (see api/geo.js — backed by the x-vercel-ip-country header Vercel
// adds automatically, same mechanism Cloudflare/Canva/Netflix use). This is
// the real signal; guessIsPH() above is only a same-render-frame placeholder
// until this resolves. Never used for anything security-sensitive — the
// server independently resolves the actual price/variant at checkout time.
// Returns null on any failure (offline, endpoint down, running locally
// without Vercel's dev proxy), so callers should keep their existing guess.
export async function detectCountryInfo() {
  try {
    const res = await fetch(apiUrl('/api/geo'));
    if (!res.ok) return null;
    const data = await res.json().catch(() => ({}));
    return { country: data.country || null, discountPercent: Number(data.discountPercent) || 0 };
  } catch {
    return null;
  }
}

export async function detectCountry() {
  const info = await detectCountryInfo();
  return info ? info.country : null;
}

// { loading, isPremium, expiresAt, source, via, lifetime }
// `lifetime` is true for a one-time Lifetime purchase (lib/grantPremium.js's
// grantLifetime). Its expiresAt is a far-future sentinel, so isPremium works
// unchanged — the flag is only for showing "Lifetime" instead of a date.
// `source` ('paid' | 'trial' | null) mirrors whatever grantPremiumDays last
// stamped on billing/status (see lib/grantPremium.js) — lets the UI show
// "Free Trial" instead of "Premium" while a trial is the active grant.
// `via` ('play' | 'paymongo' | 'lemonsqueezy' | null) is the payment RAIL
// behind the current grant — distinct from source, since a Play purchase
// and a PayMongo purchase both stamp source: 'paid'. AccountRow's delete-
// account warning uses this (rather than asking the on-device Play Billing
// plugin) to know specifically whether there's a live Play subscription it
// can't cancel on the user's behalf.
//
// `loading` stays true until the answer is real: while sign-in is still being
// restored (useAuth's own loading — before this, a cold start reported a
// settled "not premium" for a moment), and again whenever the signed-in
// account changes, until that account's billing doc arrives. Anything that
// takes something AWAY from a free user (the background reset in App.jsx)
// must wait for loading to be false.
export function usePremium() {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState({ loading: isFirebaseConfigured, isPremium: false, expiresAt: null, source: null, via: null, lifetime: false });

  useEffect(() => {
    if (isFirebaseConfigured && authLoading) return;
    if (!isFirebaseConfigured || !user) {
      setState({ loading: false, isPremium: false, expiresAt: null, source: null, via: null, lifetime: false });
      return;
    }
    setState({ loading: true, isPremium: false, expiresAt: null, source: null, via: null, lifetime: false });
    const ref = doc(db, 'users', user.uid, 'billing', 'status');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data();
        const expiresAt = data?.premiumExpiresAt || null;
        const isPremium = data?.plan === 'premium' && Boolean(expiresAt) && expiresAt > Date.now();
        setState({
          loading: false, isPremium, expiresAt, source: data?.source || null, via: data?.via || null,
          lifetime: isPremium && data?.lifetime === true,
        });
      },
      () => setState({ loading: false, isPremium: false, expiresAt: null, source: null, via: null, lifetime: false }),
    );
    return unsub;
  }, [user?.uid, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  return state;
}

// Asks the server to create a checkout session for the given provider + plan,
// then redirects the browser to its hosted payment page.
//   provider: 'paymongo' (GCash/card/Maya/QR Ph, for PH users) or
//             'lemonsqueezy' (international cards, everyone else)
//   plan:     a key from src/firebase/plans.js ('weekly' | 'monthly' | 'yearly')
// True once Lemon Squeezy (international USD payments) is actually wired up
// on the server — set REACT_APP_LEMONSQUEEZY_ENABLED=true once the account's
// requirements are approved and the LEMONSQUEEZY_* env vars are set in Vercel.
// Until then, non-PH visitors see a "coming soon" note instead of a checkout
// button that would just fail.
export function isLemonSqueezyEnabled() {
  return process.env.REACT_APP_LEMONSQUEEZY_ENABLED === 'true';
}

// Records interest from a signed-in user so we can notify them once
// international payments go live. See api/waitlist/join.js.
export async function joinLemonSqueezyWaitlist() {
  if (!isFirebaseConfigured) throw new Error('Firebase not configured');
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  const idToken = await user.getIdToken();
  const res = await fetch(apiUrl('/api/waitlist/join'), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || data.error || 'Could not join waitlist');
}

// Google Play policy forbids showing an alternative payment method inside an
// app distributed via the Play Store, so the native Android build must never
// reach PayMongo/Lemon Squeezy's hosted checkout at all — it goes through
// Play Billing instead (src/firebase/playBilling.js). Callers (PremiumGateModal,
// PremiumRow) also use this to decide whether to show the provider toggle —
// it must stay hidden on native Android, since offering a choice at all is
// itself "steering" under Play's policy.
export function isNativeAndroid() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
}

// Play-Billing-specific sibling to startCheckout() below — same call shape
// (plan id in, promise out) but never touches PayMongo/Lemon Squeezy.
export async function startPlayBillingPurchase(plan = 'monthly') {
  if (!isFirebaseConfigured) throw new Error('Firebase not configured');
  if (!auth.currentUser) throw new Error('Not signed in');
  if (!isPlayBillingAvailable()) throw new Error('Play Billing is not available on this device');
  await purchasePlayBillingPlan(plan);
}

export async function startCheckout(provider = 'paymongo', plan = 'monthly') {
  if (!isFirebaseConfigured) throw new Error('Firebase not configured');
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  // Never let a native Android session reach the hosted web checkout — Play
  // policy requires Play Billing for in-app digital purchases on that
  // platform. Callers should prefer startPlayBillingPurchase() directly on
  // native Android (see isNativeAndroid()), but this guard exists so a stray
  // call from shared code can never smuggle the PayMongo/LS flow onto Android.
  if (isNativeAndroid()) {
    return startPlayBillingPurchase(plan);
  }
  const idToken = await user.getIdToken();
  const endpoint = provider === 'lemonsqueezy'
    ? '/api/payments/create-checkout-ls'
    : '/api/payments/create-checkout';
  const res = await fetch(apiUrl(endpoint), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ plan }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.url) throw new Error(data.message || data.error || 'Could not start checkout');
  // In the native Android build, navigating the app's own WebView to
  // PayMongo's hosted page would strand the user outside the app. Open it in
  // an in-app browser tab instead — usePremium()'s onSnapshot listener keeps
  // running underneath and flips isPremium the moment the webhook writes
  // billing/status, so the app already reflects the upgrade when they close
  // the tab and come back, no deep link needed.
  if (Capacitor.isNativePlatform()) {
    await Browser.open({ url: data.url });
  } else {
    window.location.href = data.url;
  }
}
